import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InputAdornment from '@mui/material/InputAdornment';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useUserStore } from '../../store';

import { can } from '../../utils/access';
const EmployeeFormDialog = ({
  t,
  open,
  onClose,
  employee,
  onEmployeeCreated,
  onEmployeeUpdated,
}) => {
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const isAdmin = can(currentOrganization, 'employees.view_costs');
  const isEditMode = Boolean(employee);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [seatStatus, setSeatStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    title: '',
    state: 'active',
    show_in_calendar: true,
    monthly_cost: '',
  });

  useEffect(() => {
    if (employee) {
      setFormData({
        name: employee.name || '',
        phone_number: employee.phone_number || '',
        title: employee.title || '',
        state: employee.state || 'active',
        show_in_calendar: employee.show_in_calendar ?? true,
        monthly_cost: employee.monthly_cost != null ? String(employee.monthly_cost) : '',
      });
      setSeatStatus(null);
    } else {
      setFormData({
        name: '',
        phone_number: '',
        title: '',
        state: 'active',
        show_in_calendar: true,
        monthly_cost: '',
      });
      // Check seat status for new employee
      if (open) {
        fetchSeatStatus();
      }
    }
    setError(null);
  }, [employee, open]);

  const fetchSeatStatus = async () => {
    try {
      const response = await axios.get('/api/payments/seats/');
      setSeatStatus(response.data);
    } catch (err) {
      // If we can't fetch seat status, allow creation (backend will enforce)
      setSeatStatus(null);
    }
  };

  const canAddEmployee = seatStatus === null || seatStatus.can_add_employee || seatStatus.is_unlimited;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError(t('Employee name is required'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        name: formData.name.trim(),
        phone_number: formData.phone_number.trim(),
        title: formData.title.trim(),
        state: formData.state,
        show_in_calendar: formData.show_in_calendar,
      };
      if (isAdmin) {
        payload.monthly_cost = formData.monthly_cost ? parseFloat(formData.monthly_cost) : null;
      }

      let response;
      if (isEditMode) {
        response = await axios.patch(`/api/employees/${employee.id}/`, payload);
        if (onEmployeeUpdated) {
          onEmployeeUpdated(response.data);
        }
      } else {
        response = await axios.post('/api/employees/', payload);
        if (onEmployeeCreated) {
          onEmployeeCreated(response.data);
        }
      }

      onClose();
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.name) {
        setError(errorData.name[0]);
      } else if (errorData?.non_field_errors) {
        setError(errorData.non_field_errors[0]);
      } else {
        setError(err.response?.data?.error || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
          {isEditMode ? t('Edit Employee') : t('Add Employee')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {!isEditMode && !canAddEmployee && (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    onClose();
                    navigate('/settings?tab=billing');
                  }}
                >
                  {t('Upgrade Plan')}
                </Button>
              }
            >
              {t('Employee limit reached. Your plan allows {{limit}} employees and you have {{count}}.', {
                limit: seatStatus?.employees_limit || 0,
                count: seatStatus?.employees_count || 0,
              })}
            </Alert>
          )}

          <TextField
            name="name"
            label={t('Name')}
            value={formData.name}
            onChange={handleChange}
            required
            fullWidth
            autoFocus
            disabled={!isEditMode && !canAddEmployee}
            error={Boolean(error && !formData.name.trim())}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />

          <TextField
            name="title"
            label={t('employee_title')}
            placeholder={t('employee_title_placeholder')}
            value={formData.title}
            onChange={handleChange}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />

          <TextField
            name="phone_number"
            label={t('Phone Number')}
            value={formData.phone_number}
            onChange={handleChange}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />

          <FormControl fullWidth>
            <InputLabel>{t('Status')}</InputLabel>
            <Select
              name="state"
              value={formData.state}
              onChange={handleChange}
              label={t('Status')}
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="active">{t('Active')}</MenuItem>
              <MenuItem value="inactive">{t('Inactive')}</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formData.show_in_calendar}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    show_in_calendar: e.target.checked,
                  }))
                }
              />
            }
            label={t('Show in calendar')}
          />

          {isAdmin && isEditMode && (
            <TextField
              name="monthly_cost"
              label={t('employeeMonthlyCost')}
              value={formData.monthly_cost}
              onChange={handleChange}
              fullWidth
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">€/kk</InputAdornment>,
              }}
              helperText={t('employeeMonthlyCostHelp')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />
          )}

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: '10px',
            px: 3,
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || (!isEditMode && !canAddEmployee)}
          data-testid="submit-employee-button"
          startIcon={loading ? <CircularProgress size={20} /> : null}
          sx={{
            borderRadius: '10px',
            px: 3,
            bgcolor: 'secondary.main',
            '&:hover': { bgcolor: 'secondary.dark' },
          }}
        >
          {loading ? <span>{t('Saving...')}</span> : isEditMode ? <span>{t('Save Changes')}</span> : <span>{t('Add Employee')}</span>}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withTranslation()(EmployeeFormDialog);
